import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { QrCode, Camera, X, CheckCircle, AlertTriangle, Loader2, Shield, Sparkles, Wrench, User, ArrowLeft, RefreshCw } from 'lucide-react';
import QrScanner from 'qr-scanner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const WeaponScanner = () => {
  const { user, isAdmin, isOrgAdmin, isRangeOfficer } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Weapon state
  const [scannedWeapon, setScannedWeapon] = useState(null);
  const [isPersonalWeapon, setIsPersonalWeapon] = useState(false);
  const [showWeaponActions, setShowWeaponActions] = useState(false);
  const [weaponActionLoading, setWeaponActionLoading] = useState(false);
  const [weaponActionSuccess, setWeaponActionSuccess] = useState(null);
  
  // Form states
  const [cleaningNotes, setCleaningNotes] = useState('');
  const [maintenanceDescription, setMaintenanceDescription] = useState('');
  const [maintenanceCost, setMaintenanceCost] = useState('');
  const [showCleaningForm, setShowCleaningForm] = useState(false);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  
  // Camera refs
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const isProcessingRef = useRef(false);
  const isMountedRef = useRef(true);

  // Start scanner on mount with delay to avoid AbortError
  useEffect(() => {
    isMountedRef.current = true;
    
    // Delay start to ensure DOM is ready and avoid race conditions
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        startScanning();
      }
    }, 500);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      // Don't start if already scanning or component unmounted
      if (qrScannerRef.current || !isMountedRef.current) {
        return;
      }
      
      setError(null);
      setScanning(true);
      
      // Wait for video element to be available
      await new Promise(resolve => setTimeout(resolve, 200));
      
      if (!videoRef.current || !isMountedRef.current) {
        console.log('🔇 Video element not ready or component unmounted');
        return;
      }

      // Initialize QR Scanner
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        result => handleScan(result),
        {
          preferredCamera: 'environment',
          highlightScanRegion: true,
          highlightCodeOutline: true,
          returnDetailedScanResult: true
        }
      );

      await qrScannerRef.current.start();
      console.log('✅ Weapon QR Scanner started');
      
    } catch (error) {
      console.error('❌ Scanner start error:', error);
      // Only show error if it's not an AbortError (which happens during rapid mount/unmount)
      if (error.name !== 'AbortError' && isMountedRef.current) {
        setError('Kunne ikke starte kamera. Sjekk at du har gitt tilgang.');
      }
      setScanning(false);
    }
  };

  const stopScanning = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setScanning(false);
  };

  const handleScan = async (result) => {
    if (isProcessingRef.current) return;
    
    const rawCode = result?.data || result;
    if (!rawCode) return;
    
    // Trim whitespace and normalize the code
    const code = rawCode.trim();
    
    // Ignore very short codes (likely partial scans or noise)
    if (code.length < 10) {
      console.log('🔇 Ignoring short code:', code);
      return;
    }
    
    console.log('🔫 Scanned code:', code, 'Length:', code.length);
    
    let weaponQrCode = null;
    let weaponId = null;
    let personalCode = null;
    
    // Check if this is the new format (AKT-WPN-XXXXXXXX) - CLUB weapon
    if (code.startsWith('AKT-WPN-')) {
      weaponQrCode = code;
    }
    // Personal weapon code (AKT-MV-XXXXXXXX) - user's own weapon card
    else if (code.toUpperCase().startsWith('AKT-MV-')) {
      personalCode = code.toUpperCase();
    } 
    // Check if this is the old JSON format
    else if (code.startsWith('{')) {
      try {
        const parsed = JSON.parse(code);
        if (parsed.type === 'weapon' && parsed.id) {
          weaponId = parsed.id;
          console.log('📋 Old format weapon QR detected, ID:', weaponId);
        } else {
          // Valid JSON but not a weapon - show error
          console.log('⚠️ JSON detected but not weapon type');
          setError('Dette er ikke en våpen-QR-kode. Bruk den vanlige skanneren for treningsregistrering.');
          return;
        }
      } catch (e) {
        // Invalid JSON that starts with { - might be partial scan, ignore silently
        console.log('🔇 Ignoring invalid JSON (possibly partial scan)');
        return;
      }
    }
    // Check if it looks like a location/training QR code (starts with AKT-)
    else if (code.startsWith('AKT-')) {
      console.log('⚠️ Training QR code detected:', code);
      setError('Dette er en trenings-QR-kode. Bruk den vanlige skanneren for treningsregistrering.');
      return;
    }
    // Unknown format - ignore silently (might be partial scan or noise)
    else {
      console.log('🔇 Unknown code format, ignoring:', code.substring(0, 20));
      return;
    }
    
    // If neither format matched after all checks
    if (!weaponQrCode && !weaponId && !personalCode) {
      console.log('🔇 No valid weapon code found');
      return;
    }
    
    isProcessingRef.current = true;
    setIsProcessing(true);
    stopScanning();
    
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (personalCode) {
        // Personal weapon card scan
        response = await axios.get(`${API_BASE}/api/my-weapons/scan/${personalCode}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const card = response.data;
        // Normalize to the shape the UI expects
        const normalized = {
          id: card.id,
          brand: card.weapon_brand,
          model: card.weapon_model,
          caliber: card.caliber,
          serial_number: '—',
          cleaning_count: card.cleaning_count || 0,
          last_cleaned: card.last_cleaned || null,
          _personal: true,
        };
        setScannedWeapon(normalized);
        setIsPersonalWeapon(true);
        setShowWeaponActions(true);
      } else if (weaponQrCode) {
        // New format - scan by QR code (club weapon)
        response = await axios.get(`${API_BASE}/api/club-operations/weapons/scan/${weaponQrCode}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setScannedWeapon(response.data);
        setIsPersonalWeapon(false);
        setShowWeaponActions(true);
      } else {
        // Old format - get weapon by ID (club weapon)
        response = await axios.get(`${API_BASE}/api/club-operations/weapons/${weaponId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setScannedWeapon(response.data);
        setIsPersonalWeapon(false);
        setShowWeaponActions(true);
      }
      
      console.log('✅ Weapon found:', response.data);
      
    } catch (error) {
      console.error('❌ Weapon scan error:', error);
      let errorMsg = 'Kunne ikke finne våpenet.';
      
      if (error.response?.status === 404) {
        errorMsg = 'Våpenet ble ikke funnet. Sjekk at QR-koden tilhører din klubb.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      
      setError(errorMsg);
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false;
    }
  };

  // Handle weapon cleaning registration
  const handleWeaponCleaning = async () => {
    if (!scannedWeapon) return;
    
    setWeaponActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = isPersonalWeapon
        ? `${API_BASE}/api/my-weapon-cards/${scannedWeapon.id}/clean`
        : `${API_BASE}/api/club-operations/weapons/${scannedWeapon.id}/clean`;
      await axios.post(endpoint, {
        notes: cleaningNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWeaponActionSuccess({
        type: 'cleaning',
        message: 'Pussing registrert!',
        weapon: scannedWeapon
      });
      setShowCleaningForm(false);
      setCleaningNotes('');
      
    } catch (error) {
      console.error('❌ Cleaning registration error:', error);
      setError(error.response?.data?.detail || 'Kunne ikke registrere pussing');
    } finally {
      setWeaponActionLoading(false);
    }
  };

  // Handle weapon maintenance registration
  const handleWeaponMaintenance = async () => {
    if (!scannedWeapon || !maintenanceDescription) return;
    
    setWeaponActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = isPersonalWeapon
        ? `${API_BASE}/api/my-weapon-cards/${scannedWeapon.id}/maintenance`
        : `${API_BASE}/api/club-operations/weapons/${scannedWeapon.id}/maintenance`;
      await axios.post(endpoint, {
        description: maintenanceDescription,
        cost: maintenanceCost ? parseFloat(maintenanceCost) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWeaponActionSuccess({
        type: 'maintenance',
        message: 'Vedlikehold registrert!',
        weapon: scannedWeapon
      });
      setShowMaintenanceForm(false);
      setMaintenanceDescription('');
      setMaintenanceCost('');
      
    } catch (error) {
      console.error('❌ Maintenance registration error:', error);
      setError(error.response?.data?.detail || 'Kunne ikke registrere vedlikehold');
    } finally {
      setWeaponActionLoading(false);
    }
  };

  // Reset scanner state
  const resetScanner = () => {
    setShowWeaponActions(false);
    setScannedWeapon(null);
    setIsPersonalWeapon(false);
    setWeaponActionSuccess(null);
    setShowCleaningForm(false);
    setShowMaintenanceForm(false);
    setCleaningNotes('');
    setMaintenanceDescription('');
    setMaintenanceCost('');
    setError(null);
    isProcessingRef.current = false;
    startScanning();
  };

  // Auto-redirect after successful action
  // Personal weapon → /profile (regardless of role)
  // Club weapon → admins/skyteledere → /club-operations, others → /home
  const getRedirectUrl = () => {
    if (isPersonalWeapon) return '/profile';
    const hasClubOpsAccess = isAdmin || isOrgAdmin || isRangeOfficer;
    return hasClubOpsAccess ? '/club-operations' : '/home';
  };
  
  useEffect(() => {
    if (weaponActionSuccess) {
      const timer = setTimeout(() => {
        window.location.href = getRedirectUrl();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [weaponActionSuccess, isAdmin, isOrgAdmin, isRangeOfficer, isPersonalWeapon]);

  // Countdown state for success screen
  const [countdown, setCountdown] = useState(4);
  
  useEffect(() => {
    if (weaponActionSuccess) {
      setCountdown(4);
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [weaponActionSuccess]);

  // Render success screen
  if (weaponActionSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-green-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-green-200">
          <div className="flex flex-col items-center p-8 text-center">
            <div className="bg-green-500 p-4 rounded-full mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            <div className="bg-green-500 text-white px-6 py-3 rounded-full mb-6 shadow-lg">
              <span className="text-lg font-bold">
                {weaponActionSuccess.type === 'cleaning' ? '✨ PUSSING REGISTRERT!' : '🔧 VEDLIKEHOLD REGISTRERT!'}
              </span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 mb-6 w-full shadow-inner border-2 border-green-100">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Våpen:</span>
                  <span className="text-gray-900 font-bold">{weaponActionSuccess.weapon.brand} {weaponActionSuccess.weapon.model}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Kaliber:</span>
                  <span className="text-gray-900">{weaponActionSuccess.weapon.caliber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Registrert av:</span>
                  <span className="text-gray-900">{user?.full_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-600">Tidspunkt:</span>
                  <span className="text-gray-900">{new Date().toLocaleString('nb-NO')}</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 w-full">
              <p className="text-gray-500 text-sm mb-2">
                Går tilbake til oversikt om {countdown} sekunder...
              </p>
              <button
                onClick={resetScanner}
                className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-full transition-all duration-200 shadow-lg"
              >
                Skann nytt våpen
              </button>
              <a
                href={getRedirectUrl()}
                className="text-green-600 hover:text-green-700 font-medium text-center"
              >
                {(isAdmin || isOrgAdmin || isRangeOfficer) ? 'Tilbake til Klubb drift nå' : 'Tilbake til Hjem nå'}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render cleaning form
  if (showCleaningForm && scannedWeapon) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-gray-200">
          <div className="flex flex-col p-6">
            <button
              onClick={() => setShowCleaningForm(false)}
              className="self-start mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              Tilbake
            </button>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-yellow-500 p-3 rounded-full">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Registrer pussing</h2>
                <p className="text-gray-600 text-sm">{scannedWeapon.brand} {scannedWeapon.model}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notater (valgfritt)
                </label>
                <textarea
                  value={cleaningNotes}
                  onChange={(e) => setCleaningNotes(e.target.value)}
                  placeholder="F.eks. renset løp, skiftet fjær..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
              
              <button
                onClick={handleWeaponCleaning}
                disabled={weaponActionLoading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-4 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {weaponActionLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Registrerer...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Bekreft pussing
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render maintenance form
  if (showMaintenanceForm && scannedWeapon) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-gray-200">
          <div className="flex flex-col p-6">
            <button
              onClick={() => setShowMaintenanceForm(false)}
              className="self-start mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              Tilbake
            </button>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-500 p-3 rounded-full">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Registrer vedlikehold</h2>
                <p className="text-gray-600 text-sm">{scannedWeapon.brand} {scannedWeapon.model}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse av vedlikehold *
                </label>
                <textarea
                  value={maintenanceDescription}
                  onChange={(e) => setMaintenanceDescription(e.target.value)}
                  placeholder="F.eks. byttet sluttstykke, sendt til børsemaker..."
                  rows={3}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kostnad (NOK, valgfritt)
                </label>
                <input
                  type="number"
                  value={maintenanceCost}
                  onChange={(e) => setMaintenanceCost(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <button
                onClick={handleWeaponMaintenance}
                disabled={weaponActionLoading || !maintenanceDescription}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {weaponActionLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Registrerer...
                  </>
                ) : (
                  <>
                    <Wrench className="w-5 h-5" />
                    Bekreft vedlikehold
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render weapon actions popup
  if (showWeaponActions && scannedWeapon) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-gray-200 relative">
          {/* Close button */}
          <button
            onClick={resetScanner}
            className="absolute top-4 right-4 p-2 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors z-10"
            aria-label="Lukk"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
          
          <div className="flex flex-col items-center p-8 text-center">
            {/* Weapon Icon */}
            <div className="bg-yellow-500 p-4 rounded-full mb-6">
              <Shield className="w-12 h-12 text-white" />
            </div>
            
            {/* Weapon Info */}
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              {scannedWeapon.brand} {scannedWeapon.model}
            </h2>
            <p className="text-gray-600 mb-2">{scannedWeapon.caliber}</p>
            <p className="text-gray-500 text-sm mb-6 font-mono">{scannedWeapon.serial_number}</p>
            
            {/* Status badge */}
            {scannedWeapon.is_loaned ? (
              <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-6 flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="font-medium">Utlånt til: {scannedWeapon.loaned_to}</span>
              </div>
            ) : (
              <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full mb-6 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Tilgjengelig</span>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="w-full space-y-3 mb-6">
              <button
                onClick={() => setShowCleaningForm(true)}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold py-4 px-4 rounded-2xl text-lg transition-all duration-200 shadow-lg transform hover:scale-105 flex items-center justify-center gap-3"
              >
                <Sparkles className="w-6 h-6" />
                Pussing
              </button>
              
              <button
                onClick={() => setShowMaintenanceForm(true)}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-4 rounded-2xl text-lg transition-all duration-200 shadow-lg transform hover:scale-105 flex items-center justify-center gap-3"
              >
                <Wrench className="w-6 h-6" />
                Vedlikehold / Reparasjon
              </button>
              
              {!scannedWeapon.is_loaned && (isAdmin || isOrgAdmin || isRangeOfficer) && (
                <a
                  href={`/weapon-loan?weapon=${scannedWeapon.id}`}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-4 px-4 rounded-2xl text-lg transition-all duration-200 shadow-lg transform hover:scale-105 flex items-center justify-center gap-3"
                >
                  <User className="w-6 h-6" />
                  Utlån
                </a>
              )}
            </div>
            
            {/* Scan another / Back buttons */}
            <div className="flex flex-col gap-2 w-full">
              <button
                onClick={resetScanner}
                className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors"
              >
                Skann nytt våpen
              </button>
              <a
                href={getRedirectUrl()}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                {(isAdmin || isOrgAdmin || isRangeOfficer) ? 'Tilbake til Klubb drift' : 'Tilbake til Hjem'}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render scanner view
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 p-4 flex items-center justify-between">
        <a href={getRedirectUrl()} className="flex items-center gap-2 text-gray-300 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
          <span>Tilbake</span>
        </a>
        <div className="flex items-center gap-2 text-yellow-500">
          <Shield className="w-5 h-5" />
          <span className="font-bold">Våpenskanner</span>
        </div>
        <div className="w-20"></div>
      </div>

      {/* Scanner area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {error ? (
          <div className="bg-red-50 rounded-3xl max-w-sm w-full mx-4 shadow-2xl border-4 border-red-200 p-8 text-center">
            <div className="bg-red-500 p-4 rounded-full mb-6 mx-auto w-fit">
              <AlertTriangle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-xl font-bold text-red-700 mb-2">Feil</h2>
            <p className="text-red-600 mb-6">{error}</p>
            <button
              onClick={resetScanner}
              className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-full transition-all"
            >
              Prøv igjen
            </button>
          </div>
        ) : (
          <div className="w-full max-w-md">
            {/* Instructions */}
            <div className="text-center mb-6">
              <div className="bg-yellow-500 p-4 rounded-full mb-4 mx-auto w-fit">
                <QrCode className="w-10 h-10 text-gray-900" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Skann våpen-QR</h1>
              <p className="text-gray-400">Hold kameraet over QR-koden på et klubbvåpen eller eget våpen</p>
            </div>

            {/* Camera view */}
            <div className="relative bg-black rounded-2xl overflow-hidden aspect-square shadow-2xl border-4 border-yellow-500">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
              />
              
              {/* Scanning overlay */}
              {isProcessing && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mx-auto mb-4" />
                    <p className="text-white font-medium">Laster våpeninfo...</p>
                  </div>
                </div>
              )}
              
              {/* Corner guides */}
              <div className="absolute inset-8 pointer-events-none">
                <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-yellow-500 rounded-tl-lg"></div>
                <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-yellow-500 rounded-tr-lg"></div>
                <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-yellow-500 rounded-bl-lg"></div>
                <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-yellow-500 rounded-br-lg"></div>
              </div>
            </div>

            {/* Help text */}
            <p className="text-center text-gray-500 text-sm mt-4">
              QR-koden finnes i våpenkofferten eller på våpenet
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeaponScanner;
